import { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useHasPermission } from "../../common/Hooks";
import EnvironmentTemplate from "../../services/environmentTemplate";
import PubSub from "../../services/PubSub";
import Utils from "../../utils/utils";
import List from "../controls/List";
import Toolbar from "../controls/Toolbar";
import { useTranslation } from "react-i18next";

const EnvironmentTemplateList = ({ onClickNew, onClick = () => { } }) => {
    const [translate] = useTranslation();
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [environmentTemplates, setEnvironmentTemplates] = useState([]);
    const [selectedEnvironmentTemplate, setSelectedEnvironmentTemplate] = useState([]);
    const [environmentTemplateParams, setEnvironmentTemplateParams] = useState({
        pageIncrement: 0
    });

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "domain"
    });
    const history = useHistory();

    const getEnvironments = useCallback(async (params) => {
        setEnvironmentTemplates([]);
        const response = await EnvironmentTemplate.getPaginatedEnvironmentTemplates(null, params.firstCursor, params.lastCursor, params.pageIncrement < 0, params.getLatest);

        if (response && !response.error) {
            setEnvironmentTemplates(response.data);
            setCurrentPage(page => {
                const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : params.environmentDeleted ? 1 : page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });
        }

        setEnvironmentTemplateParams(null);
    }, []);

    useEffect(() => {
        if (permissions.read && environmentTemplateParams) {
            getEnvironments(environmentTemplateParams);
        }
    }, [permissions, environmentTemplateParams, getEnvironments]);

    useEffect(() => {
        const subscribeId = PubSub.subscribe("refresh-environment-template-list", (params) => {
            setEnvironmentTemplateParams({
                pageIncrement: 0,
                firstCursor: params?.environmentDeleted ? "" : cursors.firstCursor,
                lastCursor: params?.environmentDeleted ? "" : cursors.lastCursor,
                newEnvironmentId: params?.newEnvironmentId,
                environmentDeleted: params?.environmentDeleted,
                getLatest: !!params?.newEnvironmentId
            });
        });

        return () => {
            PubSub.unsubscribe("refresh-environment-template-list", subscribeId);
        };
    }, [cursors.firstCursor, cursors.lastCursor]);

    const previous = useCallback(() => {
        setEnvironmentTemplateParams({
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setEnvironmentTemplateParams({
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const handleClick = useCallback((_id) => {
        setSelectedEnvironmentTemplate(_id);
        history.push(`/environment-templates/${_id}`);
        onClick();
    }, [history, onClick]);

    const renderToolbar = useMemo(() => {
        return <Toolbar
            buttons={[
                {
                    name: "ChevronLeft",
                    title: translate("OTM_PREVIOUS"),
                    onClick: previous,
                    disabled: currentPage === 1
                },
                {
                    name: "ChevronRight",
                    title: translate("OTM_NEXT"),
                    onClick: next,
                    disabled: !hasNextPage
                },
                {
                    name: "Add",
                    title: translate("OTM_NEW"),
                    onClick: onClickNew,
                    disabled: !permissions.create,
                }
            ]}
            heading={translate("OTM_ENVIRONMENT_TEMPLATES")}
        />
    }, [translate, previous, currentPage, next, hasNextPage, onClickNew, permissions]);

    return (
        <div className="card">
            <div className="card-header border-bottom">
                {renderToolbar}
            </div>
            <div className="card-body">
                <List
                    onClick={handleClick}
                    attr="name"
                    data={environmentTemplates}
                    selectedElement={selectedEnvironmentTemplate} />
            </div>
        </div>
    );
};

export default EnvironmentTemplateList;
